import {
  Dialog,
  FormControl,
  IconButton,
  InputBase,
  makeStyles,
  MenuItem,
  Select,
  Typography,
  withStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import gql from 'graphql-tag';
import get from 'lodash/get';
import range from 'lodash/range';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { memberClient } from 'src/config/Apollo';
import { colors } from 'src/themes/colors';
import { Dispatch } from 'src/config/Store';
import { getCurrentUser } from 'src/selectors/userSelectors';

import MHButton from '../MHButton';

const CustomInput = withStyles((theme) => ({
  root: {
    'label + &': {
      marginTop: theme.spacing(3),
    },
  },
  input: {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '8px 20px 4px 5px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}))(InputBase);

const useStyles = makeStyles({
  title: {
    fontFamily: 'Avenir-Black',
    fontSize: 25,
    color: colors.black4,
    marginBottom: 20,
  },
  dialogTitle: {
    position: 'relative',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: 40,
  },
  formControl: {
    width: 215,
  },
  cardBackButton: {
    fontFamily: 'Avenir-Black',
    fontSize: 15,
    width: '100%',
    color: colors.gray11,
    backgroundColor: colors.gray10,
    '&:hover': {
      backgroundColor: colors.gray12,
    },
  },
  cardButton: {
    fontFamily: 'Avenir-Black',
    width: '100%',
    fontSize: 15,
  },
  closeButton: {
    position: 'absolute',
    right: 5,
    top: 5,
  },
  buttonsWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 5,
    paddingTop: 25,
  },
  selectItem: {
    padding: '0px 10px',
    fontSize: 16,
  },
  filterSelect: {
    maxHeight: 100,
    overflow: 'auto',
    marginTop: 15,
    marginBottom: 15,
    width: 200,
    border: `1px solid ${colors.gray12}`,
    borderRadius: 3,
    '&:before': {
      border: 0,
    },
  },
});

const saveNewClientCapacity = gql`
  mutation saveNewClientCapacity(
    $updatedNewClientCapacity: Int!
    $providerSchedulingType: String!
  ) {
    saveNewClientCapacity(
      updatedNewClientCapacity: $updatedNewClientCapacity
      providerSchedulingType: $providerSchedulingType
    ) {
      newClientCapacity
      providerSchedulingType
    }
  }
`;

enum HowManyClientsModalTestID {
  Container = 'HowManyClientsModalTestID.Container',
  CloseButton = 'HowManyClientsModalTestID.CloseButton',
  BackButton = 'HowManyClientsModalTestID.BackButton',
  ContinueButton = 'HowManyClientsModalTestID.ContinueButton',
  NumberDropdown = 'HowManyClientsModalTestID.NumberDropdown',
  NumberPrefix = 'HowManyClientsModalTestID.NumberPrefix',
}

interface Props {
  onBack: () => void;
  onClose: () => void;
}

const options = range(0, 51);

export const HowManyClientsModal: React.FC<Props> = ({ onClose, onBack }) => {
  const user = useSelector(getCurrentUser);

  const [clientsAmount, setClientsAmount] = useState(user.newClientCapacity || 0);
  const [saving, setSaving] = useState(false);

  const dispatch: Dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();

  const handleContinue = async () => {
    if (saving) {
      return;
    }

    try {
      setSaving(true);

      await memberClient.mutate({
        mutation: saveNewClientCapacity,
        variables: { updatedNewClientCapacity: clientsAmount, providerSchedulingType: 'Roster' },
      });

      await dispatch.user.fetchCurrentUser();

      onClose();
    } catch (error) {
      setSaving(false);

      const gqlError = get(error, 'message');
      const gqlErrorMessage = gqlError && gqlError.split('GraphQL error: ')[1];

      const errorMessage =
        gqlErrorMessage || 'An unexpected error has occurred. Please try again later.';

      enqueueSnackbar(errorMessage, {
        variant: 'error',
        anchorOrigin: {
          horizontal: 'center',
          vertical: 'top',
        },
      });
    }
  };

  return (
    <Dialog
      open
      fullWidth={false}
      aria-labelledby="form-dialog-title"
      maxWidth="md"
      onClose={onClose}
      data-testid={HowManyClientsModalTestID.Container}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        className={classes.closeButton}
        data-testid={HowManyClientsModalTestID.CloseButton}
      >
        <CloseIcon />
      </IconButton>

      <div className={classes.dialogContent}>
        <Typography className={classes.title}>How many new clients can you book?</Typography>

        <FormControl className={classes.formControl}>
          <Select
            value={clientsAmount}
            onChange={(e) => setClientsAmount(e.target.value as number)}
            placeholder="Select number"
            input={<CustomInput />}
            data-testid={HowManyClientsModalTestID.NumberDropdown}
          >
            {options.map((num) => (
              <MenuItem
                value={num}
                key={num}
                data-testid={`HowManyClientsModalTestID.NumberPrefix-${num}`}
              >
                <Typography className={classes.selectItem}>{num}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className={classes.buttonsWrapper}>
          <MHButton
            className={classes.cardBackButton}
            onClick={onBack}
            wrapperWidth="47%"
            data-testid={HowManyClientsModalTestID.BackButton}
          >
            Back
          </MHButton>
          <MHButton
            className={classes.cardButton}
            onClick={handleContinue}
            wrapperWidth="47%"
            loading={saving}
            data-testid={HowManyClientsModalTestID.ContinueButton}
          >
            Continue
          </MHButton>
        </div>
      </div>
    </Dialog>
  );
};
